/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  fetchWindcaveTransactionId,
  formatNumber,
  parseFlickError,
  reportEligibility,
  sendAnalyticsPageEvent,
  sendEligibility,
  updateCurrentFormStep,
} from "lib/util";
import { Action } from "re-reduced";
import type { SagaIterator } from "redux-saga";
import {
  all,
  call,
  put,
  select,
  takeEvery,
  takeLatest,
} from "redux-saga/effects";

import { ROUTES } from "../configuration";
import actions from "./actions";
import * as api from "./api";
import {
  getCreationSource,
  getFlickUserId,
  getLeadSource,
  getPromoCode,
  getSelectedAddress,
  getSelectedEligibilityType,
  getSignUpState,
  getUser,
} from "./selectors";
import {
  AddressType,
  BillEstimatePayload,
  BillEstimateResponse,
  EligibilityType,
  fetchProductPayload,
  GetSignUpResponse,
  SignUpResponse,
  SupplyAddresses,
  User,
  WindcaveAuthorisation,
  WindcaveTransaction,
} from "./types";

export default function* CoreSagaWatcher(): SagaIterator {
  yield all([
    takeLatest(actions.validateAddress.type, validateAddress),
    takeLatest(
      actions.createWindcaveTransaction.type,
      createWindcaveTransaction,
    ),
    takeLatest(
      actions.checkSuccessfulAuthorisation.type,
      checkSuccessfulAuthorisation,
    ),
    takeLatest(actions.createFlickSignUp.type, createFlickSignUp),
    takeLatest(actions.updateFlickSignUp.type, updateFlickSignUp),
    takeLatest(actions.getFlickSignUp.type, getFlickSignUp),
    takeLatest(actions.selectAddress.type, fetchBillingEstimates),
    takeLatest(actions.selectAddress.type, fetchAvailableProducts),
    takeLatest(actions.fetchBillingEstimate.type, fetchBillingEstimates),
    takeEvery(actions.fetchProductData.type, fetchProductData),
    takeEvery(actions.createPlanChooserSignUp.type, createPlanChooserSignUp),
    takeEvery(actions.setCurrentFormStep.type, setCurrentFormStepCookie),
    takeEvery(actions.clearState.type, resetCookieState),
  ]);
}

function* validateAddress(action: Action<AddressType>): SagaIterator {
  yield put(actions.validateAddress.request());
  try {
    const response: SupplyAddresses = yield call(
      api.validateAddress,
      action.payload,
    );
    yield put(
      actions.validateAddress.success({
        eligibleAddresses: response,
      }),
    );

    if (response.data.length >= 1) {
      // More than one result, match the original payload information
      // based on street and unit identifier.
      const matchIndex = response.included.findIndex(
        (dataset) =>
          dataset.attributes.unit_identifier?.toLowerCase() ===
            action.payload.unit?.toLowerCase() &&
          dataset.attributes.number === action.payload.number,
      );

      reportEligibility(
        "Address search",
        response.data[matchIndex].attributes.eligibility,
      );

      const { city, number, street, suburb, postcode, unit_identifier } =
        response.included[matchIndex].attributes;
      yield put(
        actions.selectAddress({
          address: {
            city,
            number,
            street,
            suburb,
            icp: response.data[matchIndex].attributes.ea_icp_id,
            fullAddress: "",
            postcode: postcode ?? "",
            unit: unit_identifier,
          },
          eligibility: response.data[matchIndex].attributes.eligibility,
        }),
      );
    }

    // No results, select address with eligibility unknown
    if (response.data.length === 0) {
      sendEligibility("Address search", "Not found");

      yield put(
        actions.selectAddress({
          address: {
            ...action.payload,
          },
          eligibility: undefined,
        }),
      );
    }
  } catch (err: any) {
    yield put(actions.validateAddress.failure(err));
  }
}

function* fetchAvailableProducts(
  action: Action<BillEstimatePayload>,
): SagaIterator {
  yield put(actions.fetchBillingEstimate.request());

  const address: AddressType = yield select(getSelectedAddress);
  const icp = action.payload.icp || address.icp;

  if (icp) {
    const { consumption, duration } = action.payload;
    try {
      const response: BillEstimateResponse = yield call(
        api.fetchBillingEstimate,
        { icp, consumption: consumption, duration: duration },
      );
      yield put(actions.fetchBillingEstimate.success(response));
    } catch (err: any) {
      sendAnalyticsPageEvent("Error", "Failed bill estimate", icp);
      yield put(actions.fetchBillingEstimate.failure(err));
    }
  } else {
    yield put(actions.fetchBillingEstimate.failure(new Error("ICP not found")));
  }
}

function* fetchBillingEstimates(
  action: Action<BillEstimatePayload>,
): SagaIterator {
  const eligibility: EligibilityType = yield select(getSelectedEligibilityType);
  const address: AddressType = yield select(getSelectedAddress);
  if (eligibility === "success" || eligibility === "manual") {
    yield put(actions.fetchBillingEstimate.request());

    const icp = action.payload.icp || address.icp;

    if (icp) {
      try {
        const response: BillEstimateResponse = yield call(
          api.fetchBillingEstimate,
          {
            icp,
            consumption: action.payload.consumption,
            duration: action.payload.duration,
          },
        );
        yield put(actions.fetchBillingEstimate.success(response));
      } catch (err: any) {
        if (
          err?.response?.data?.errors[0]?.meta
            ?.location_classification_from_supply_node === "commercial"
        ) {
          reportEligibility("Commercial", "reject");
          yield put(
            actions.updateEligibility({ eligibility: "reject_commercial" }),
          );
        } else {
          sendAnalyticsPageEvent("Error", "Failed bill estimate", icp);
          yield put(actions.fetchBillingEstimate.failure(err));
        }
      }
    } else {
      yield put(
        actions.fetchBillingEstimate.failure(new Error("ICP not found")),
      );
    }
  }
}

function* createWindcaveTransaction(): SagaIterator {
  yield put(actions.createWindcaveTransaction.request());

  try {
    const id = yield select(getFlickUserId);
    const response: WindcaveTransaction = yield call(
      api.createWindcaveTransaction,
      id,
    );
    localStorage.setItem("wctID", response.data.id);
    yield put(actions.createWindcaveTransaction.success(response));
    window.open(response.data.attributes.url, "_self");
  } catch (err: any) {
    yield put(actions.createWindcaveTransaction.failure(err));
  }
}

function* checkSuccessfulAuthorisation(): SagaIterator {
  yield put(actions.checkSuccessfulAuthorisation.request());
  const id = yield call(fetchWindcaveTransactionId);
  try {
    const response: WindcaveAuthorisation = yield call(
      api.getWindcaveAuthorisation,
      id,
    );
    if (response.data.attributes.result === "APPROVED") {
      yield put(actions.checkSuccessfulAuthorisation.success(response));
    } else {
      yield put(
        actions.checkSuccessfulAuthorisation.failure(
          new Error("Error - Transaction was not successful"),
        ),
      );
    }
  } catch (err: any) {
    yield put(actions.checkSuccessfulAuthorisation.failure(err));
  }
}

function* createFlickSignUp(): SagaIterator {
  yield put(actions.createFlickSignUp.request());
  const addressDetails: AddressType = yield select(getSelectedAddress);
  const creationSource = yield select(getCreationSource);
  const signUpState = yield select(getSignUpState);
  const promoCode = yield select(getPromoCode);
  try {
    const response: SignUpResponse = yield call(api.createUserFlickSignUp, {
      addressDetails,
      creationSource,
      signUpState: signUpState,
      promoCode,
    });
    // then reset it to the id returned from this sign up call.
    localStorage.setItem("signUpId", response.data.id);
    yield put(actions.createFlickSignUp.success(response));
  } catch (err: any) {
    const errorMessage = parseFlickError(err?.response);
    yield put(actions.createFlickSignUp.failure(errorMessage));
  }
}

function* createPlanChooserSignUp(action: Action<AddressType>): SagaIterator {
  yield call(validateAddress, action);
  yield call(createFlickSignUp);
}

function* setCurrentFormStepCookie(action: Action<string>): SagaIterator {
  yield call(updateCurrentFormStep, action.payload);
}

function* resetCookieState(): SagaIterator {
  yield call(updateCurrentFormStep, ROUTES.root);
  localStorage.removeItem("signUpId");
  localStorage.removeItem("wctID");
}

function* updateFlickSignUp(): SagaIterator {
  yield put(actions.updateFlickSignUp.request());
  const addressDetails: AddressType = yield select(getSelectedAddress);
  const user: User = yield select(getUser);
  const id: string = yield select(getFlickUserId);
  const creationSource = yield select(getCreationSource);
  const leadSource = yield select(getLeadSource);
  const signUpState = yield select(getSignUpState);
  const promoCode = yield select(getPromoCode);

  try {
    const response: SignUpResponse = yield call(api.updateUserFlickSignUp, {
      id,
      addressDetails,
      details: {
        ...user.details,
        mobileNumber: formatNumber(user.details.mobileNumber),
        homeNumber: user.details.homeNumber,
      },
      payment: user.payment,
      property: user.property,
      checks: user.checks,
      creationSource,
      leadSource,
      signUpState: signUpState,
      promoCode,
    });
    yield put(actions.updateFlickSignUp.success(response));
  } catch (err: any) {
    const errorMessage = parseFlickError(err?.response);
    yield put(actions.updateFlickSignUp.failure(errorMessage));
  }
}

function* getFlickSignUp(action: Action<string>): SagaIterator {
  yield put(actions.getFlickSignUp.request());

  try {
    const response: GetSignUpResponse = yield call(
      api.getUserFlickSignUp,
      action.payload,
    );
    const signUpAttributes = response.data.attributes;

    yield put(
      actions.setAddress({
        city: signUpAttributes.city,
        number: signUpAttributes.street_number,
        street: signUpAttributes.street,
        suburb: signUpAttributes.suburb,
        icp: signUpAttributes.icp,
        fullAddress: "",
        postcode: signUpAttributes.postcode,
        unit: signUpAttributes.unit,
      }),
    );
    yield put(
      actions.setUserDetails({
        dob: signUpAttributes.date_of_birth,
        emailAddress: signUpAttributes.email,
        firstName: signUpAttributes.first_name,
        lastName: signUpAttributes.last_name,
        prefName: signUpAttributes.preferred_name,
        mobileNumber: signUpAttributes.primary_phone,
        homeNumber: signUpAttributes.secondary_phone,
        companyName: signUpAttributes.company_name,
        product: signUpAttributes.selected_product,
      }),
    );
    yield put(
      actions.setUserProperty({
        moveDate: signUpAttributes?.moving_in_date || undefined,
        switchDate: signUpAttributes?.requested_switch_date || undefined,
        usageType: signUpAttributes.usage_type,
        medicallyDependent: signUpAttributes?.medical_dependency,
        situation: signUpAttributes.requested_switch_date
          ? "switch_provider"
          : "moved_in",
      }),
    );
    yield put(actions.setPromoCode(signUpAttributes.promo_code || undefined));
    yield put(actions.setLeadSource(signUpAttributes.lead_source || undefined));
    yield put(
      actions.setUserChecks({
        marketingAccepted: true,
        smsSwitchNotificationsAccepted: signUpAttributes.switch_in_sms_opt_in,
        isCommercialAccount: signUpAttributes.selected_product === "business",
        termsAccepted: signUpAttributes.terms_accepted,
        creditCheckAccepted: signUpAttributes.credit_check_accepted,
      }),
    );
    yield put(
      actions.setUserBillingPeriod(
        signUpAttributes.billing_period || undefined,
      ),
    );
    const currentFormStep =
      localStorage.getItem("currentFormStep") || ROUTES.root;
    yield put(actions.setCurrentFormStep(currentFormStep));
    yield put(actions.getFlickSignUp.success(response));
  } catch (err: any) {
    const errorMessage = parseFlickError(err?.response);
    yield put(actions.getFlickSignUp.failure(errorMessage));
  }
}

function* fetchProductData(action: Action<fetchProductPayload>): SagaIterator {
  yield put(actions.fetchProductData.request());
  try {
    const response = yield call(api.fetchProductData, action.payload);
    yield put(
      actions.fetchProductData.success(response.data.planCollection.items),
    );
  } catch (err: any) {
    yield put(actions.fetchProductData.failure(err));
  }
}
